// Google Ads campaign → kyck Account attribution (§3b for Google). PURE + tested.
//
// The same problem Meta has, with the same shape and the same answer. Several
// kyck locations can share ONE Google Ads customer id (the live book already has
// "Noonan SYD CLUBS", "Snap Fitness Casey Gyms (BHZ Group)", "Snap Fitness - WA -
// Amar Group", "Snap Fitness Vinay Accounts", "Corporate - Snap - AU"), so a
// `customer_id → one account` map is not enough: every campaign under a shared
// customer would land on whichever location happened to be picked, and a Brand or
// AM filter would then show one location another location's spend.
//
// So attribution is per-CAMPAIGN, resolved by matching the campaign NAME against
// each candidate location's match tokens — the identical device the Meta side
// uses, and it works naturally on Google because campaign names embed the
// location ("Kyck | PMAX | Cromer").
//
// ⚠️ TOKENS ARE SHARED WITH META (`Account.adAccountMatchTokens`), deliberately.
// The tokens are location identifiers (suburb / club names) that appear in BOTH
// platforms' campaign names, so a separate `googleAdsMatchTokens` field would
// double the admin work per location and immediately drift out of sync — the
// worse failure, because a stale Google token set attributes silently and wrongly.
// If Google naming ever genuinely diverges from Meta's, add an OPTIONAL override
// field and fall back to these; do not fork the concept by default.
//
// ⚠️ NEVER GUESS. A campaign matching two locations is a `collision` and is
// attributed to NEITHER — an even split or a first-match win would put real money
// on the wrong client's report. Unattributed and collision campaigns still count
// in the agency total but carry no location, so they'd silently disappear from
// every filtered view; `buildAttributionHealth` is what makes that visible.

import { hasMatchToken } from './adAccountSharing.js';
import type {
  GoogleAdsAccountDimensions,
  GoogleAdsAttributionStatus,
  GoogleAdsCampaignAttribution,
} from '../types/googleAdsReporting.js';

/**
 * The synthetic id of the "needs match token" tree node — the one place every
 * campaign we could NOT attribute is collected (Phase 2c).
 *
 * ⚠️ It is a SENTINEL, never a real kyck Account id. Grouping the tree by Location
 * means an unattributable campaign has no node to sit under, and the tempting
 * options are both wrong: dropping it makes the tree quietly under-report against
 * the KPI tiles, and inventing a Location for it puts real money on a client's
 * report. So it gets its own visible node, and focusing that node drills into
 * exactly the campaigns that need a token — the fix, not just the symptom.
 *
 * Double-underscored so it cannot collide with a Firestore document id.
 */
export const UNATTRIBUTED_LOCATION_ID = '__unattributed__';

/** True when an id refers to the synthetic gap node rather than a real Location. */
export function isUnattributedLocationId(accountId: string | null | undefined): boolean {
  return accountId === UNATTRIBUTED_LOCATION_ID;
}

/** The roster shape attribution needs — a lean subset of Account + resolved dims. */
export interface AttributionAccount extends GoogleAdsAccountDimensions {
  googleAdsCustomerId: string | null;
  matchTokens: readonly string[] | null;
}

/** A campaign as it arrives from the `google_ads.campaigns` view. */
export interface AttributionCampaign {
  customerId: string;
  campaignId: string;
  campaignName: string;
}

/**
 * Google shows customer ids as `123-456-7890` but the API and our
 * `Account.googleAdsCustomerId` store bare digits. Normalising to digits means a
 * hand-typed dashed id still matches — and an empty/garbage value normalises to
 * null rather than to the empty string, which would otherwise group every
 * unset account into one giant fake "shared" cluster.
 */
export function normalizeCustomerId(id: string | null | undefined): string | null {
  if (id == null) return null;
  const digits = String(id).replace(/\D/g, '');
  return digits === '' ? null : digits;
}

/** Lowercased, trimmed, non-empty tokens — the same cleaning the Meta view does. */
export function cleanTokens(tokens: readonly string[] | null | undefined): string[] {
  return (tokens ?? []).map((t) => t.trim().toLowerCase()).filter((t) => t !== '');
}

/**
 * Group the roster by normalized customer id. Accounts with no Google customer
 * id are skipped — they claim nothing. Built over the FULL roster (never a page
 * slice) so "is this customer shared" is a roster-wide truth, exactly as
 * buildAdAccountHolderCounts is on the Meta side.
 */
export function buildCustomerHolders(
  accounts: readonly AttributionAccount[],
): Map<string, AttributionAccount[]> {
  const byCustomer = new Map<string, AttributionAccount[]>();
  for (const account of accounts) {
    const key = normalizeCustomerId(account.googleAdsCustomerId);
    if (!key) continue;
    const list = byCustomer.get(key);
    if (list) list.push(account);
    else byCustomer.set(key, [account]);
  }
  return byCustomer;
}

/** Case-insensitive substring match of any token against the campaign name. */
export function campaignMatchesTokens(
  campaignName: string,
  tokens: readonly string[] | null | undefined,
): boolean {
  const cleaned = cleanTokens(tokens);
  if (cleaned.length === 0) return false;
  const haystack = campaignName.toLowerCase();
  return cleaned.some((t) => haystack.includes(t));
}

/** Strip the fields attribution carries internally, leaving the wire dimensions. */
function toDimensions(a: AttributionAccount): GoogleAdsAccountDimensions {
  return {
    accountId: a.accountId,
    accountName: a.accountName,
    brandAccountId: a.brandAccountId,
    brandName: a.brandName,
    groupAccountId: a.groupAccountId,
    groupName: a.groupName,
    industry: a.industry,
    accountManagerId: a.accountManagerId,
    accountManagerName: a.accountManagerName,
    country: a.country,
  };
}

/**
 * Resolve ONE campaign to its owning location.
 *
 * Standalone (exactly one holder) short-circuits BEFORE any token matching — a
 * location that owns its customer id outright needs no tokens, so requiring them
 * would strand the majority of the book (most clients have their own account).
 */
export function attributeCampaign(
  campaign: AttributionCampaign,
  holders: ReadonlyMap<string, AttributionAccount[]>,
): GoogleAdsCampaignAttribution {
  const customerId = normalizeCustomerId(campaign.customerId) ?? campaign.customerId;
  const candidates = holders.get(customerId) ?? [];

  const base = {
    customerId,
    campaignId: campaign.campaignId,
    campaignName: campaign.campaignName,
    collidingAccountNames: [] as string[],
  };

  if (candidates.length === 0) {
    return { ...base, status: 'unlinked', account: null };
  }
  const sole = candidates[0];
  if (candidates.length === 1 && sole) {
    return { ...base, status: 'standalone', account: toDimensions(sole) };
  }

  const matched = candidates.filter((a) => campaignMatchesTokens(campaign.campaignName, a.matchTokens));
  const single = matched[0];
  if (matched.length === 1 && single) {
    return { ...base, status: 'token', account: toDimensions(single) };
  }
  if (matched.length === 0) {
    return { ...base, status: 'unattributed', account: null };
  }
  return {
    ...base,
    status: 'collision',
    account: null,
    // Sorted so the collision report reads the same way every render.
    collidingAccountNames: matched
      .map((a) => a.accountName ?? a.accountId)
      .sort((x, y) => x.localeCompare(y)),
  };
}

/** Resolve every campaign. The directory the whole dashboard filters through. */
export function attributeCampaigns(
  campaigns: readonly AttributionCampaign[],
  accounts: readonly AttributionAccount[],
): GoogleAdsCampaignAttribution[] {
  const holders = buildCustomerHolders(accounts);
  return campaigns.map((c) => attributeCampaign(c, holders));
}

/**
 * A location that SHARES a Google customer id but carries NO match token is an
 * attribution gap — none of its campaigns can ever be resolved to it. Mirrors the
 * Meta grid's "shared ad account — match token missing" flag so the same fix
 * (add a token) applies on both platforms.
 */
export function sharedAccountsMissingTokens(
  accounts: readonly AttributionAccount[],
): AttributionAccount[] {
  const holders = buildCustomerHolders(accounts);
  const gaps: AttributionAccount[] = [];
  for (const list of holders.values()) {
    if (list.length < 2) continue;
    for (const a of list) if (!hasMatchToken(a.matchTokens)) gaps.push(a);
  }
  return gaps;
}

// ---------------------------------------------------------------------------
// Filtering the directory by the page's account-level dimensions
// ---------------------------------------------------------------------------

/** The account-level filter set. Empty / omitted = no constraint. */
export interface AttributionFilter {
  brandAccountIds?: string[];
  groupAccountIds?: string[];
  industries?: string[];
  accountManagerIds?: string[];
  accountIds?: string[];
  /**
   * Country is an ACCOUNT-level dimension like the rest, so it resolves through
   * attribution too — filtering to Australia on a SHARED Google customer keeps
   * only the campaigns whose names attribute them to an Australian location,
   * rather than the whole shared account.
   */
  countries?: string[];
}

const passes = (selected: string[] | undefined, value: string | null): boolean => {
  if (!selected || selected.length === 0) return true;
  return value != null && selected.includes(value);
};

/** True when any account-level filter is actually constraining the view. */
export function hasAccountFilters(filter: AttributionFilter): boolean {
  return (
    (filter.brandAccountIds?.length ?? 0) > 0 ||
    (filter.groupAccountIds?.length ?? 0) > 0 ||
    (filter.industries?.length ?? 0) > 0 ||
    (filter.accountManagerIds?.length ?? 0) > 0 ||
    (filter.accountIds?.length ?? 0) > 0 ||
    (filter.countries?.length ?? 0) > 0
  );
}

/**
 * Which campaigns survive the account-level filters.
 *
 * ⚠️ A campaign with NO attributed location (unattributed / collision / unlinked)
 * fails EVERY account-level filter, exactly as the Meta attributed view's NULL
 * metadata does. That is correct — we genuinely don't know it belongs to the
 * selected brand — and it is why the attribution-health block exists: without it
 * the spend would just quietly go missing from a filtered total.
 */
export function filterAttributedCampaigns(
  directory: readonly GoogleAdsCampaignAttribution[],
  filter: AttributionFilter,
): GoogleAdsCampaignAttribution[] {
  if (!hasAccountFilters(filter)) return [...directory];
  return directory.filter((c) => {
    const a = c.account;
    if (!a) return false;
    return (
      passes(filter.brandAccountIds, a.brandAccountId) &&
      passes(filter.groupAccountIds, a.groupAccountId) &&
      passes(filter.industries, a.industry) &&
      passes(filter.accountManagerIds, a.accountManagerId) &&
      passes(filter.accountIds, a.accountId) &&
      passes(filter.countries, a.country)
    );
  });
}

/** The distinct attributed locations in a directory — feeds the filter dropdowns. */
export function directoryAccounts(
  directory: readonly GoogleAdsCampaignAttribution[],
): GoogleAdsAccountDimensions[] {
  const byId = new Map<string, GoogleAdsAccountDimensions>();
  for (const c of directory) {
    if (c.account && !byId.has(c.account.accountId)) byId.set(c.account.accountId, c.account);
  }
  return [...byId.values()].sort((a, b) =>
    (a.accountName ?? '').localeCompare(b.accountName ?? ''),
  );
}
